import cx from "classnames";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ReactNode, useEffect } from "react";
import { isNativeMobileApp } from "utils/platform.helpers";

interface DraggableModalProps {
  /** Header */
  header?: ReactNode;
  /** Body */
  children: ReactNode;
  /** Footer */
  footer?: ReactNode;
  /** Open/hide */
  open?: boolean;
  /** On close callback */
  onClose?: () => void;
  /** Specify the size */
  size?: string;
  /** Modal class name */
  modalClassName?: string;
  /** Header class name */
  headerClassName?: string;
  /** Body class name */
  bodyClassName?: string;
  /** Footer class name */
  footerClassName?: string;
  /** Animation duration in seconds */
  animationDuration?: number;
  /** If false, modal cannot be closed (disables drag and backdrop click) */
  canClose?: boolean;
}

/**
 * A draggable modal that can be closed by dragging down
 */
export const DraggableModal = ({
  header,
  children,
  footer,
  open,
  onClose,
  size,
  modalClassName = "rounded-t-lg",
  headerClassName,
  bodyClassName,
  footerClassName,
  animationDuration = 0.3,
  canClose = true,
}: DraggableModalProps) => {
  const prefersReducedMotion = useReducedMotion();
  const dragEnabled = canClose && isNativeMobileApp();
  // Add overflow-hidden to #root when the modal is open
  // Note: We target #root instead of body because body has overflow:hidden
  // for mobile browser viewport fixes
  useEffect(() => {
    const root = document.getElementById("root");
    if (open) {
      root?.classList.add("overflow-hidden");
    } else {
      root?.classList.remove("overflow-hidden");
    }

    return () => {
      root?.classList.remove("overflow-hidden");
    };
  }, [open]);

  const handleClose = () => {
    if (canClose) {
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-transparent"
            onClick={canClose ? handleClose : undefined}
            data-testid="div-dismiss-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0 : animationDuration * 0.67,
              ease: "easeInOut",
            }}
          />
          {/* See `Modal` — positioning moves to a wrapper so the same panel is a
              drag-to-dismiss sheet on a phone and a centred dialog on desktop. */}
          <div className="pointer-events-none fixed inset-0 z-50 flex flex-col items-center justify-end desk:justify-center desk:p-8">
            <motion.div
              className={cx(
                "pointer-events-auto flex max-h-[calc(100%_-_env(safe-area-inset-top)_-_12px)] w-full flex-col bg-white desk:max-h-full desk:min-h-0 desk:w-[440px] desk:!transform-none desk:overflow-hidden desk:rounded-xl desk:shadow-xl sm:w-[390px]",
                size,
                modalClassName,
              )}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0 : animationDuration,
                ease: "easeInOut",
              }}
              drag={dragEnabled ? "y" : false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={
                dragEnabled
                  ? (_, info) => {
                      // Close modal if dragged down more than 100px or with sufficient velocity
                      if (info.offset.y > 100 || info.velocity.y > 500) {
                        handleClose();
                      }
                    }
                  : undefined
              }
            >
              {/* Drag indicator - only show if modal can be closed */}
              {dragEnabled && (
                <div
                  className="flex w-full justify-center py-2 desk:hidden"
                  data-testid="draggable-modal-handle"
                >
                  <div className="h-1 w-10 rounded-full bg-gray-300" />
                </div>
              )}

              <div className={cx("flex-none", headerClassName)}>{header}</div>
              <div className={cx("grow overflow-auto overscroll-contain", bodyClassName)}>
                {children}
              </div>
              <div className={cx("flex-none pb-[env(safe-area-inset-bottom)]", footerClassName)}>
                {footer}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
